import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { CommunityTimePeriod } from '@/mocks/communityAnalytics';
import { getCommunityAnalytics, communityKpis, communityTimePeriodLabels } from '@/mocks/communityAnalytics';
import CommunityKpiCard from './components/CommunityKpiCard';
import CommunityTopsSection from './components/CommunityTopsSection';

type LoadState = 'loading' | 'ready' | 'error' | 'empty';

export default function CommunityAnalyticsPage() {
  const [dataState, setDataState] = useState<LoadState>('loading');
  const [analyticsData, setAnalyticsData] = useState<ReturnType<typeof getCommunityAnalytics> | null>(null);
  const [timePeriod, setTimePeriod] = useState<CommunityTimePeriod>('semana');
  const [activeTab, setActiveTab] = useState<string>('miembros');

  const loadData = useCallback(() => {
    setDataState('loading');
    setTimeout(() => {
      const shouldError = Math.random() < 0.06;
      const shouldBeEmpty = Math.random() < 0.03;
      if (shouldError) {
        setDataState('error');
        setAnalyticsData(null);
      } else if (shouldBeEmpty) {
        setDataState('empty');
        setAnalyticsData(null);
      } else {
        const data = getCommunityAnalytics();
        setAnalyticsData(data);
        setDataState('ready');
      }
    }, 700);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const retry = () => loadData();

  /* ═══════ LOADING ═══════ */
  if (dataState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
              <div className="w-28 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-56 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
              <div className="bg-background-50 rounded-2xl border border-background-200 p-6 animate-pulse">
                <div className="w-56 h-5 rounded-md bg-background-200 mb-3"></div>
                <div className="w-80 h-3 rounded bg-background-200"></div>
                <div className="flex gap-2 mt-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-20 h-9 rounded-full bg-background-200"></div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-background-50 rounded-xl border border-background-200 p-4 animate-pulse">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-16 h-3 rounded bg-background-200"></div>
                      <div className="w-8 h-8 rounded-lg bg-background-200"></div>
                    </div>
                    <div className="w-20 h-7 rounded-md bg-background-200 mb-2"></div>
                    <div className="w-24 h-3 rounded bg-background-200"></div>
                  </div>
                ))}
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200 p-5 animate-pulse">
                <div className="w-32 h-4 rounded-md bg-background-200 mb-4"></div>
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((j) => (
                    <div key={j} className="w-full h-5 rounded-md bg-background-200"></div>
                  ))}
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ═══════ ERROR ═══════ */
  if (dataState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center">
                <i className="ri-error-warning-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Comunidad</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center max-w-sm mx-auto px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-base mb-2">Error al cargar datos</h3>
              <p className="text-foreground-500 text-sm mb-5">No se pudieron cargar las métricas de comunidad. Intentá de nuevo.</p>
              <button
                onClick={retry}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ═══════ EMPTY ═══════ */
  if (dataState === 'empty') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-secondary-500 flex items-center justify-center">
                <i className="ri-group-line text-background-50 text-xs"></i>
              </div>
              <h1 className="text-foreground-950 font-bold text-sm">Comunidad</h1>
            </div>
          </div>
        </header>
        <div className="flex">
  
          <main className="flex-1 min-w-0 flex items-center justify-center py-24">
            <div className="text-center max-w-sm mx-auto px-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
                <i className="ri-group-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="text-foreground-950 font-bold text-base mb-2">Sin datos de comunidad</h3>
              <p className="text-foreground-500 text-sm mb-5">
                Todavía no hay suficiente interacción en la comunidad. Los datos aparecerán cuando los oyentes empiecen a participar.
              </p>
              <button
                onClick={retry}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* ═══════ READY ═══════ */
  const presetLabel = communityTimePeriodLabels[timePeriod];

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-secondary-500 flex items-center justify-center">
              <i className="ri-group-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Comunidad</h1>
              <p className="text-foreground-400 text-[9px]">{presetLabel}</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors">
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Volver al sitio</span>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Analytics de Comunidad</h2>
              <p className="text-foreground-400 text-xs">
                Interacción y engagement de tu audiencia · {presetLabel}
              </p>
            </div>
            <Link
              to="/"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line text-xs"></i>
              Ver sitio
            </Link>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
            {/* Hero + Time Selector */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-5 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-secondary-100 flex items-center justify-center shrink-0">
                    <i className="ri-heart-pulse-line text-xl text-secondary-500"></i>
                  </div>
                  <div>
                    <h3 className="text-foreground-950 font-extrabold text-lg">
                      Interacción de la Comunidad
                    </h3>
                    <p className="text-foreground-400 text-xs mt-0.5">
                      Midiendo la participación real de tu audiencia ·{' '}
                      <strong className="text-foreground-700">{presetLabel.toLowerCase()}</strong>
                    </p>
                  </div>
                </div>

                {/* Time selector */}
                <div className="flex items-center bg-background-100 rounded-full p-1 shrink-0">
                  {(Object.entries(communityTimePeriodLabels) as [CommunityTimePeriod, string][]).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setTimePeriod(key)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                        timePeriod === key
                          ? 'bg-background-50 text-foreground-950 shadow-sm'
                          : 'text-foreground-400 hover:text-foreground-600'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* KPI Cards Grid - 5 columns on desktop */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {communityKpis.map((kpi) => (
                <CommunityKpiCard key={kpi.id} kpi={kpi} timePeriod={timePeriod} />
              ))}
            </div>

            {/* Tops Section */}
            {analyticsData && (
              <CommunityTopsSection
                data={analyticsData.tops}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            )}

            {/* Highlight cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
                  <i className="ri-hand-heart-line text-rose-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-foreground-400 text-[10px] font-medium">Mayor interacción</p>
                  <p className="text-foreground-950 text-sm font-bold">Saludos (+31.7%)</p>
                  <p className="text-foreground-400 text-[9px]">Crecimiento anual destacado</p>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                  <i className="ri-share-forward-line text-amber-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-foreground-400 text-[10px] font-medium">Mayor viralidad</p>
                  <p className="text-foreground-950 text-sm font-bold">Compartidos (+35.2%)</p>
                  <p className="text-foreground-400 text-[9px]">Crecimiento anual destacado</p>
                </div>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                  <i className="ri-music-2-line text-emerald-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-foreground-400 text-[10px] font-medium">Pedidos activos</p>
                  <p className="text-foreground-950 text-sm font-bold">9,400 canciones</p>
                  <p className="text-foreground-400 text-[9px]">Total anual acumulado</p>
                </div>
              </div>
            </div>

            {/* Footer badge */}
            <div className="bg-secondary-50 border border-secondary-200 rounded-xl p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
                    <i className="ri-group-line text-secondary-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-secondary-800 text-xs font-semibold">Panel de Comunidad</p>
                    <p className="text-secondary-600/80 text-[10px]">Métricas de interacción real. No medimos solo oyentes, medimos participación.</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-secondary-200 text-secondary-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1 w-fit">
                  <i className="ri-time-line text-[9px]"></i>
                  Actualizado ahora
                </span>
              </div>
            </div>
          </div>
        </main>
      </div>


    </div>
  );
}